import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, FileText, Trash2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function PianiSanitariPage() {
  const [piani, setPiani] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sicurezza/piani-sanitari');
      setPiani(data);
    } catch (e) {
      toast.error('Impossibile caricare i piani: ' + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, titolo) => {
    if (!window.confirm(`Eliminare il piano "${titolo}"?`)) return;
    try {
      await api.delete(`/sicurezza/piani-sanitari/${id}`);
      toast.success('Piano eliminato');
      load();
    } catch (e) {
      toast.error('Errore eliminazione: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const classeBadge = (classe) => {
    const colors = { A: 'bg-green-100 text-green-800 border-green-300', B: 'bg-amber-100 text-amber-800 border-amber-300', C: 'bg-red-100 text-red-800 border-red-300' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${colors[classe] || 'bg-slate-100'}`}>Classe {classe}</span>;
  };

  return (
    <div className="space-y-4 p-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold" data-testid="piani-sanitari-title">Piani Sanitari Eventi</h1>
          <p className="text-sm text-slate-600">Gestione dei piani sanitari da inviare al C.O. 118 — calcolo rischio Algoritmo di Maurer</p>
        </div>
        <Link to="/sicurezza/piani-sanitari/nuovo">
          <Button data-testid="new-piano-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo Piano Sanitario</Button>
        </Link>
      </div>

      {loading && <div className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>}

      {!loading && piani.length === 0 && (
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-slate-600">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p>Nessun piano sanitario creato.</p>
            <p className="text-xs mt-1">Clicca "Nuovo Piano Sanitario" per crearne uno.</p>
          </CardContent>
        </Card>
      )}

      {!loading && piani.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{piani.length} Piani registrati</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left">
                  <th className="p-2 text-xs">Evento</th>
                  <th className="p-2 text-xs">Data</th>
                  <th className="p-2 text-xs">Luogo</th>
                  <th className="p-2 text-xs">Organizzazione</th>
                  <th className="p-2 text-xs">Rischio</th>
                  <th className="p-2 text-xs">Stato</th>
                  <th className="p-2 text-xs text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {piani.map(p => (
                  <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`piano-row-${p.id}`}>
                    <td className="p-2 font-medium">{p.evento_titolo || '—'}</td>
                    <td className="p-2">{p.evento_data}</td>
                    <td className="p-2 text-slate-600">{p.evento_luogo_citta}</td>
                    <td className="p-2 text-slate-600">{p.associazione_denominazione}</td>
                    <td className="p-2">{p.rischio?.classe ? classeBadge(p.rischio.classe) : '—'} <span className="text-xs text-slate-500 ml-1">{p.rischio?.totale}</span></td>
                    <td className="p-2"><Badge variant="outline" className="text-xs">{p.stato}</Badge></td>
                    <td className="p-2 text-right space-x-1">
                      <Link to={`/sicurezza/piani-sanitari/${p.id}`}>
                        <Button size="sm" variant="outline" data-testid={`edit-${p.id}`}>Apri</Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id, p.evento_titolo)} className="text-red-600" data-testid={`delete-${p.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
